'use client';

import { useState, useMemo, useCallback } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { RequestCard, type RequestCardData } from './RequestCard';
import { MagicLinkForm } from './MagicLinkForm';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface RequestListClientProps {
  initialRequests: RequestCardData[];
  portalSlug: string;
  currentVisitorId: string | null;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  searchQuery: string;
  initialStatus?: string;
  requestsLabel?: string;
}

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'planned', label: 'Planned' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
];

const SORT_OPTIONS = [
  { value: 'votes', label: 'Most Voted' },
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
];

export function RequestListClient({
  initialRequests,
  portalSlug,
  currentVisitorId,
  currentPage,
  totalPages,
  totalCount,
  searchQuery: initialSearchQuery,
  initialStatus = 'all',
  requestsLabel = 'Feature Requests',
}: RequestListClientProps) {
  const requestsLabelLower = requestsLabel.toLowerCase();
  const router = useRouter();
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [sortBy, setSortBy] = useState('votes');
  const [searchInput, setSearchInput] = useState(initialSearchQuery);
  const [showAuthDialog, setShowAuthDialog] = useState(false);

  const handleAuthRequired = useCallback(() => {
    setShowAuthDialog(true);
  }, []);

  const buildUrl = useCallback((overrides: { q?: string; page?: number; status?: string }) => {
    const params = new URLSearchParams();
    const q = overrides.q !== undefined ? overrides.q : initialSearchQuery;
    const page = overrides.page !== undefined ? overrides.page : currentPage;
    const status = overrides.status !== undefined ? overrides.status : statusFilter;
    if (q) params.set('q', q);
    if (page > 1) params.set('page', String(page));
    if (status && status !== 'all') params.set('status', status);
    const qs = params.toString();
    return `/p/${portalSlug}${qs ? `?${qs}` : ''}`;
  }, [portalSlug, initialSearchQuery, currentPage, statusFilter]);

  const handleStatusChange = useCallback((value: string) => {
    setStatusFilter(value);
    window.history.replaceState(null, '', buildUrl({ status: value, page: 1 }));
  }, [buildUrl]);

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    router.push(buildUrl({ q: searchInput.trim(), page: 1 }));
  }, [searchInput, buildUrl, router]);

  const handlePageChange = useCallback((newPage: number) => {
    router.push(buildUrl({ page: newPage }));
  }, [buildUrl, router]);

  const filteredRequests = useMemo(() => {
    let filtered = initialRequests;

    // Filter by status (client-side within current page)
    if (statusFilter !== 'all') {
      filtered = filtered.filter((r) => r.status === statusFilter);
    }

    // Sort
    const sorted = [...filtered];
    switch (sortBy) {
      case 'newest':
        sorted.sort((a, b) => {
          const aTime = a.createdAt ? new Date(a.createdAt as string | number).getTime() : 0;
          const bTime = b.createdAt ? new Date(b.createdAt as string | number).getTime() : 0;
          return bTime - aTime;
        });
        break;
      case 'oldest':
        sorted.sort((a, b) => {
          const aTime = a.createdAt ? new Date(a.createdAt as string | number).getTime() : 0;
          const bTime = b.createdAt ? new Date(b.createdAt as string | number).getTime() : 0;
          return aTime - bTime;
        });
        break;
      case 'votes':
      default:
        sorted.sort((a, b) => (b.voteCount ?? 0) - (a.voteCount ?? 0));
        break;
    }

    return sorted;
  }, [initialRequests, statusFilter, sortBy]);

  return (
    <div className="space-y-6">
      <div className="rounded-xl border bg-card p-4 sm:p-6 space-y-5 shadow-sm">
      {/* Controls row */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <Tabs value={statusFilter} onValueChange={handleStatusChange}>
          <TabsList>
            {STATUS_FILTERS.map((f) => (
              <TabsTrigger key={f.value} value={f.value}>
                {f.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="relative">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          type="text"
          aria-label={`Search ${requestsLabelLower}`}
          placeholder={`Search ${requestsLabelLower}...`}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="flex h-10 w-full rounded-md border border-input bg-background pl-10 pr-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-all duration-200"
        />
      </form>

      {/* Request list */}
      <div className="space-y-3">
        {filteredRequests.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-primary">
                <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h3 className="text-base font-medium mb-1">No {requestsLabelLower} found</h3>
            <p className="text-sm text-muted-foreground mb-4">Be the first to share an idea or suggestion.</p>
            <Link
              href={`/p/${portalSlug}/submit`}
              className="bg-primary text-primary-foreground px-4 py-2 rounded-md font-medium hover:bg-primary/90 text-sm transition-all duration-200"
            >
              Submit Request
            </Link>
          </div>
        ) : (
          filteredRequests.map((request) => (
            <RequestCard
              key={request.id}
              request={request}
              portalSlug={portalSlug}
              onAuthRequired={handleAuthRequired}
            />
          ))
        )}
      </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-sm text-muted-foreground">
            {totalCount} request{totalCount !== 1 ? 's' : ''}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className="px-3 py-1.5 text-sm rounded-md border border-input bg-background hover:bg-accent disabled:opacity-50 disabled:pointer-events-none transition-colors"
            >
              Previous
            </button>
            <span className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="px-3 py-1.5 text-sm rounded-md border border-input bg-background hover:bg-accent disabled:opacity-50 disabled:pointer-events-none transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Auth dialog */}
      <Dialog open={showAuthDialog} onOpenChange={setShowAuthDialog}>
        <DialogContent className="max-w-sm sm:max-w-md">
          <DialogHeader className="text-center sm:text-center">
            <DialogTitle>Sign in to vote</DialogTitle>
            <DialogDescription>Enter your email to receive a magic link</DialogDescription>
          </DialogHeader>
          <MagicLinkForm portalSlug={portalSlug} />
          <button
            onClick={() => setShowAuthDialog(false)}
            className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
          >
            Cancel
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
